"use client";

import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FaBriefcase, FaCalendarAlt, FaMapMarkerAlt, FaSearch } from "react-icons/fa";
import Header from "../../components/IntranetHeader";
import Footer from "../../components/Footer";
import { supabase } from "../../supabaseClient";

function formatDate(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export default function Vacancies() {
  const [vacancies, setVacancies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    fetchVacancies();
  }, []);

  async function fetchVacancies() {
    setLoading(true);
    setError("");

    const { data, error: fetchError } = await supabase
      .from("dc_vacancies")
      .select(`
        id,
        title,
        description,
        department,
        location,
        closing_date,
        status,
        is_active,
        created_at
      `)
      .eq("status", "Open")
      .eq("is_active", true)
      .order("created_at", { ascending: false });

    if (fetchError) {
      console.error("Unable to load vacancies:", fetchError);
      setError(fetchError.message || "Vacancies could not be loaded.");
      setVacancies([]);
    } else {
      const now = Date.now();
      setVacancies(
        (data || []).filter((vacancy) => {
          if (!vacancy.closing_date) return true;
          const closing = new Date(vacancy.closing_date).getTime();
          return Number.isNaN(closing) || closing > now;
        }),
      );
    }

    setLoading(false);
  }

  const filteredVacancies = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return vacancies;

    return vacancies.filter((vacancy) =>
      [vacancy.title, vacancy.description, vacancy.department, vacancy.location]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query)),
    );
  }, [vacancies, search]);

  return (
    <div className="flex flex-col min-h-screen">
      <Header />

      <main className="flex-1 pguh-staff-vacancies-page">
        <div className="nhsuk-width-container">
          <section className="pguh-staff-vacancies-hero">
            <div>
              <span className="pguh-eyebrow">Staff opportunities</span>
              <h1>Vacancies</h1>
              <p>
                Explore current internal opportunities across Royal University Hospitals Trust and submit your application online.
              </p>
            </div>
            <div className="pguh-staff-vacancies-hero__count">
              <span>Open roles</span>
              <strong>{vacancies.length}</strong>
            </div>
          </section>

          <section className="pguh-staff-vacancies-toolbar" aria-label="Vacancy search">
            <label>
              <FaSearch aria-hidden="true" />
              <span className="nhsuk-u-visually-hidden">Search vacancies</span>
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search by role, department or location"
              />
            </label>
          </section>

          {error && (
            <div className="nhsuk-error-summary" role="alert">
              <h2 className="nhsuk-error-summary__title">Vacancies could not be loaded</h2>
              <div className="nhsuk-error-summary__body">{error}</div>
            </div>
          )}

          {loading ? (
            <div className="pguh-staff-vacancies-empty">Loading vacancies…</div>
          ) : filteredVacancies.length === 0 ? (
            <div className="pguh-staff-vacancies-empty">
              <FaBriefcase aria-hidden="true" />
              <h2>{search ? "No matching vacancies" : "There are currently no vacancies"}</h2>
              <p>
                {search
                  ? "Try a different role, department or location."
                  : "New internal opportunities will appear here when they are published."}
              </p>
            </div>
          ) : (
            <div className="pguh-staff-vacancies-list">
              {filteredVacancies.map((vacancy) => (
                <article className="pguh-staff-vacancy-card" key={vacancy.id}>
                  <div className="pguh-staff-vacancy-card__body">
                    <div className="pguh-staff-vacancy-card__heading">
                      <span>Open</span>
                      <h2>{vacancy.title}</h2>
                    </div>

                    <p className="pguh-staff-vacancy-card__description">
                      {vacancy.description || "Further information is available on the vacancy page."}
                    </p>

                    <div className="pguh-staff-vacancy-card__meta">
                      {vacancy.department && (
                        <span>
                          <FaBriefcase aria-hidden="true" /> {vacancy.department}
                        </span>
                      )}
                      {vacancy.location && (
                        <span>
                          <FaMapMarkerAlt aria-hidden="true" /> {vacancy.location}
                        </span>
                      )}
                      <span>
                        <FaCalendarAlt aria-hidden="true" />
                        {vacancy.closing_date
                          ? `Closes ${formatDate(vacancy.closing_date)}`
                          : `Posted ${formatDate(vacancy.created_at) || "recently"}`}
                      </span>
                    </div>
                  </div>

                  <div className="pguh-staff-vacancy-card__action">
                    <Link to={`/intranet/vacancy/${vacancy.id}`}>
                      View vacancy & apply
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </main>

      <Footer />

      <style>{`
        .pguh-staff-vacancies-page { padding: 34px 0 64px; background: var(--nhs-light-grey); }
        .pguh-staff-vacancies-hero { display: flex; align-items: flex-end; justify-content: space-between; gap: 30px; margin-bottom: 20px; padding: 30px 34px; border-left: 8px solid var(--nhs-light-blue); color: #fff; background: linear-gradient(115deg, var(--nhs-dark-blue), var(--nhs-blue)); box-shadow: var(--pguh-shadow); }
        .pguh-staff-vacancies-hero h1, .pguh-staff-vacancies-hero p, .pguh-staff-vacancies-hero .pguh-eyebrow { color: #fff; }
        .pguh-staff-vacancies-hero h1 { margin: 0 0 8px; }
        .pguh-staff-vacancies-hero p { max-width: 760px; margin: 0; }
        .pguh-staff-vacancies-hero__count { display: flex; min-width: 130px; flex-direction: column; padding: 13px 16px; border: 1px solid rgba(255,255,255,.35); background: rgba(0,48,135,.3); }
        .pguh-staff-vacancies-hero__count span { color: rgba(255,255,255,.8); font-weight: 700; }
        .pguh-staff-vacancies-hero__count strong { color: #fff; font-size: 2rem; }
        .pguh-staff-vacancies-toolbar { margin-bottom: 20px; padding: 16px; border: 1px solid var(--pguh-border); background: #fff; box-shadow: var(--pguh-shadow); }
        .pguh-staff-vacancies-toolbar label { position: relative; display: flex; align-items: center; }
        .pguh-staff-vacancies-toolbar svg { position: absolute; left: 14px; color: var(--nhs-dark-grey); }
        .pguh-staff-vacancies-toolbar input { width: 100%; min-height: 46px; border: 2px solid var(--nhs-dark-grey); padding: 8px 12px 8px 42px; }
        .pguh-staff-vacancies-list { display: grid; gap: 16px; }
        .pguh-staff-vacancy-card { display: grid; grid-template-columns: minmax(0, 1fr) 210px; border: 1px solid var(--pguh-border); border-left: 6px solid var(--nhs-blue); background: #fff; box-shadow: var(--pguh-shadow); }
        .pguh-staff-vacancy-card__body { padding: 22px 24px; }
        .pguh-staff-vacancy-card__heading > span { display: inline-flex; padding: 3px 8px; color: #00401e; background: #cce5d6; font-size: .72rem; font-weight: 700; text-transform: uppercase; }
        .pguh-staff-vacancy-card__heading h2 { margin: 7px 0 0; font-size: 1.35rem; }
        .pguh-staff-vacancy-card__description { max-width: 850px; margin: 12px 0 16px; color: var(--nhs-dark-grey); }
        .pguh-staff-vacancy-card__meta { display: flex; flex-wrap: wrap; gap: 8px 18px; }
        .pguh-staff-vacancy-card__meta span { display: inline-flex; align-items: center; gap: 7px; color: var(--nhs-dark-grey); font-size: .88rem; font-weight: 700; }
        .pguh-staff-vacancy-card__meta svg { color: var(--nhs-blue); }
        .pguh-staff-vacancy-card__action { display: grid; place-items: center; padding: 20px; border-left: 1px solid var(--pguh-border); background: #f7fafc; }
        .pguh-staff-vacancy-card__action a { display: inline-flex; width: 100%; min-height: 44px; align-items: center; justify-content: center; padding: 8px 12px; border: 2px solid var(--nhs-blue); color: #fff !important; background: var(--nhs-blue); font-weight: 700; text-align: center; text-decoration: none; }
        .pguh-staff-vacancy-card__action a:hover { background: var(--nhs-dark-blue); }
        .pguh-staff-vacancies-empty { display: grid; min-height: 280px; place-items: center; align-content: center; padding: 36px; border: 1px solid var(--pguh-border); background: #fff; color: var(--nhs-dark-grey); text-align: center; box-shadow: var(--pguh-shadow); }
        .pguh-staff-vacancies-empty > svg { margin-bottom: 12px; color: var(--nhs-mid-grey); font-size: 2.2rem; }
        .pguh-staff-vacancies-empty h2 { margin: 0 0 7px; }
        .pguh-staff-vacancies-empty p { margin: 0; }
        @media (max-width: 760px) {
          .pguh-staff-vacancies-hero { align-items: flex-start; flex-direction: column; padding: 24px 20px; }
          .pguh-staff-vacancies-hero__count { width: 100%; }
          .pguh-staff-vacancy-card { grid-template-columns: 1fr; }
          .pguh-staff-vacancy-card__action { border-top: 1px solid var(--pguh-border); border-left: 0; }
        }
      `}</style>
    </div>
  );
}
